import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListSubheader from 'LuxKit/List/ListSubheader'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import OfferAirportSelectOption from './OfferAirportSelectOption'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useAirportSearch from 'hooks/Flights/useAirportSearch'
import { fetchPopularAirports } from 'actions/FlightActions'
import OfferPageDispatchContext from 'contexts/OfferPage/offerPageDispatchContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import noop from 'lib/function/noop'
import styled from 'styled-components'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import NoAirportsOptionListItem from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/NoAirportsOptionListItem'
import OfferAirportOptionsGroupedList from './OfferAirportOptionsGroupedList'
import { airportLocationToAirport } from 'lib/flights/flightUtils'
import usePopularAirports from 'hooks/Flights/usePopularAirports'

const Wrapper = styled.div`
  position: relative; /* to make the <LoadingIndicator> work  */
`

interface Props {
  searchPhrase?: string
  selectedAirport?: App.Airport
  popularAirports: App.PopularAirports
  onSelectAirport?: (airport: App.AirportLocation) => void
  charterBundleRoute?: App.CharterBundleRoute
}

function OfferAirportOptionsList(props: Props) {
  const {
    searchPhrase = '',
    selectedAirport,
    popularAirports,
    onSelectAirport = noop,
    charterBundleRoute,
  } = props

  const dispatch = useAppDispatch()
  const offerPageDispatch = useContext(OfferPageDispatchContext)

  // Get all Australian airports for charter bundles
  const australianAirports = useAppSelector(state => state.geo.originAirports)
  const popularDepartureAirports = usePopularAirports('departure')
  const airportRanking = useMemo(() => {
    const ranking = new Map<string, number>()
    popularDepartureAirports.forEach((airport, index) => {
      ranking.set(airport.airportCode, index)
    })
    return ranking
  }, [popularDepartureAirports])

  // For charter bundles, show all Australian airports (already in App.Airport format)
  const charterAirports = useMemo(() => {
    if (charterBundleRoute) {
      return australianAirports
        .filter(airport => airport.countryCode === 'AU')
        .slice()
        .sort((a, b) => {
          const rankA = airportRanking.get(a.code)
          const rankB = airportRanking.get(b.code)

          if (rankA !== undefined && rankB !== undefined) {
            return rankA - rankB
          }

          if (rankA !== undefined) {
            return -1
          }

          if (rankB !== undefined) {
            return 1
          }

          return a.name.localeCompare(b.name)
        })
    }
    return []
  }, [charterBundleRoute, australianAirports, airportRanking])

  useEffect(() => {
    dispatch(fetchPopularAirports())
  }, [dispatch])

  const [searchResults, loading] = useAirportSearch(searchPhrase)

  // Convert AirportLocation[] to Airport[] for the grouped list component
  const airports = useMemo(() => {
    return searchResults.map(airport => ({
      code: airport.airportCode,
      name: airport.airportName,
      latitude: airport.lt!,
      longitude: airport.lg!,
      countryCode: airport.country,
    } as App.Airport))
  }, [searchResults])

  const handleItemClick = useCallback((airport: App.AirportLocation) => {
    offerPageDispatch({
      type: OfferPageActions.setAirport,
      airport: airportLocationToAirport(airport),
    })
    offerPageDispatch({
      type: OfferPageActions.setFlights,
      flights: true,
    })
    onSelectAirport(airport)
  }, [offerPageDispatch, onSelectAirport])

  const airportListLabel = charterBundleRoute ? 'Australian airports' : 'Airports near you'

  const airportsList = charterBundleRoute ? (
    <OfferAirportOptionsGroupedList
      airports={charterAirports}
      handleItemClick={handleItemClick}
      selectedAirport={selectedAirport}
    />
  ) : (
    popularAirports.origins.map(airport => <OfferAirportSelectOption
      key={airport.airportCode}
      airport={airport}
      onClick={handleItemClick}
      selected={airport.airportCode === selectedAirport?.code}
      data-testid={`airport-select-${airport.airportCode}`}
    />)
  )

  return <Wrapper>
    <LoadingIndicator floating visible={loading} />
    {searchPhrase.length < 2 && <VerticalSpacer gap={8}>
      <ListSubheader colour="neutral-three">{airportListLabel}</ListSubheader>
      <div>
        {airportsList}
      </div>
    </VerticalSpacer>}

    {loading && !airports.length && <ListItemLoadingSkeleton size="large" title="8ch" subtitle="3ch" startIcon />}

    {!loading && !airports.length && !!searchPhrase && <NoAirportsOptionListItem inputValue={searchPhrase} />}

    {searchPhrase.length > 1 && <OfferAirportOptionsGroupedList
      airports={airports}
      handleItemClick={handleItemClick}
      selectedAirport={selectedAirport}
    />}
  </Wrapper>
}

export default withOfferPageAndReduxState((state, appState) => ({
  popularAirports: appState.flights.popularAirports,
  selectedAirport: state.airport,
  charterBundleRoute: state.charterBundleRoute,
}), OfferAirportOptionsList)
